import type { Touch } from 'react';
import React, { Component } from 'react';
import { getParentPosition } from '../utility/dom-helpers';

export type ScrollElementProps = {
  children: React.ReactNode;
  width: number;
  scrollRef: (el: HTMLDivElement | null) => void;
  isInteractingWithItem: boolean;
  onZoom: (touchDistanceRatio: number, xRatio: number) => void;
  onWheelZoom: (speed: number, xPosition: number, deltaY: number) => void;
  onScroll: (scrollX: number) => void;
  onScrollTime: (deltaT: number) => void;
  onResizeSidebar: (resize: null | boolean, pageX: number) => void;
  timeFromItemEvent: (e: React.MouseEvent) => number;
};

class ScrollElement extends Component<
  ScrollElementProps,
  {
    isDragging: boolean;
  }
> {
  scrollComponent: HTMLDivElement | null;
  resize: boolean | null = null;
  dragTime: number;
  lastTouchDistance: number | null;
  singleTouchStart: {
    x: number;
    y: number;
    screenY: number;
  } | null;
  lastSingleTouch: {
    x: number;
    y: number;
  } | null;

  constructor(props: ScrollElementProps) {
    super(props);
    this.state = {
      isDragging: false,
    };
  }

  /**
   * needed to handle scrolling with trackpad
   */
  handleScroll = () => {
    const component = this.scrollComponent;
    if (!component) {
      return;
    }
    this.props.onScroll(component.scrollLeft);
  };

  refHandler = (el: HTMLDivElement | null) => {
    this.scrollComponent = el;
    this.props.scrollRef(el);
    if (el) {
      el.addEventListener('wheel', this.handleWheel, { passive: false });
    }
  };

  handleWheel = (e: WheelEvent) => {
    // zoom in the time dimension
    if (e.ctrlKey || e.metaKey || e.altKey) {
      e.preventDefault();
      const parentPosition = getParentPosition(e.currentTarget as HTMLElement);
      const xPosition = e.clientX - parentPosition.x;

      const speed = e.ctrlKey ? 10 : e.metaKey ? 3 : 1;

      // convert vertical zoom to horizontal
      this.props.onWheelZoom(speed, xPosition, e.deltaY);
    } else if (e.shiftKey) {
      e.preventDefault();
      // shift+scroll event from a touchpad has deltaY property populated; shift+scroll event from a mouse has deltaX
      const component = this.scrollComponent;
      if (!component) {
        return;
      }
      this.props.onScroll(component.scrollLeft + (e.deltaY || e.deltaX));
      // no modifier pressed? we prevented the default event, so scroll or zoom as needed
    }
  };

  handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as Element;
    if (target.className === 'rct-sidebar-resize') {
      this.resize = target.classList.contains('rct-sidebar-resize-right');
      return;
    }
    if (e.button === 0) {
      this.dragTime = this.props.timeFromItemEvent(e);
      this.setState({
        isDragging: true,
      });
    }
  };

  handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (this.resize !== null) {
      this.props.onResizeSidebar(this.resize, e.pageX);
    } else if (this.state.isDragging && !this.props.isInteractingWithItem) {
      const dragTime = this.props.timeFromItemEvent(e);
      this.props.onScrollTime(this.dragTime - dragTime);
    }
  };

  handleMouseUp = () => {
    this.resize = null;
    this.setState({
      isDragging: false,
    });
  };

  handleMouseLeave = () => {
    this.resize = null;
    this.setState({
      isDragging: false,
    });
  };

  handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    const target = e.target as Element;
    if (target.className === 'rct-sidebar-resize') {
      this.resize = target.classList.contains('rct-sidebar-resize-right');
      return;
    } else {
      this.resize = null;
    }
    if (e.touches.length === 2) {
      e.preventDefault();

      this.lastTouchDistance = Math.abs(
        e.touches[0].screenX - e.touches[1].screenX,
      );
      this.singleTouchStart = null;
      this.lastSingleTouch = null;
    } else if (e.touches.length === 1) {
      e.preventDefault();

      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;

      this.lastTouchDistance = null;
      this.singleTouchStart = { x: x, y: y, screenY: window.pageYOffset };
      this.lastSingleTouch = { x: x, y: y };
    }
  };

  handleTouchMove = (e: React.TouchEvent<HTMLDivElement> & Touch) => {
    const { isInteractingWithItem, width, onZoom } = this.props;
    if (isInteractingWithItem) {
      e.preventDefault();
      return;
    }
    if (this.resize !== null) {
      const target = e.target as Element;
      this.resize = target.classList.contains('rct-sidebar-resize-right');
      this.props.onResizeSidebar(this.resize, e.pageX);
      return;
    }
    if (this.lastTouchDistance && e.touches.length === 2) {
      e.preventDefault();
      const touchDistance = Math.abs(
        e.touches[0].screenX - e.touches[1].screenX,
      );

      const parentPosition = getParentPosition(e.currentTarget);
      const xPosition =
        (e.touches[0].screenX + e.touches[1].screenX) / 2 - parentPosition.x;
      if (touchDistance !== 0 && this.lastTouchDistance !== 0) {
        onZoom(this.lastTouchDistance / touchDistance, xPosition / width);
        this.lastTouchDistance = touchDistance;
      }
    } else if (
      this.lastSingleTouch &&
      this.singleTouchStart &&
      e.touches.length === 1
    ) {
      e.preventDefault();
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const deltaX = x - this.lastSingleTouch.x;
      const deltaX0 = x - this.singleTouchStart.x;
      const deltaY0 = y - this.singleTouchStart.y;
      this.lastSingleTouch = { x: x, y: y };
      const moveX = Math.abs(deltaX0) * 3 > Math.abs(deltaY0);
      const moveY = Math.abs(deltaY0) * 3 > Math.abs(deltaX0);
      if (deltaX !== 0 && moveX) {
        const component = this.scrollComponent;
        if (component) {
          this.props.onScroll(component.scrollLeft - deltaX);
        }
      }
      if (moveY) {
        window.scrollTo(
          window.pageXOffset,
          this.singleTouchStart.screenY - deltaY0,
        );
      }
    }
  };

  handleTouchEnd = () => {
    if (this.lastTouchDistance) {
      this.lastTouchDistance = null;
    }
    if (this.lastSingleTouch) {
      this.lastSingleTouch = null;
      this.singleTouchStart = null;
    }
    this.resize = null;
  };

  componentWillUnmount() {
    if (this.scrollComponent) {
      this.scrollComponent.removeEventListener('wheel', this.handleWheel);
    }
  }

  render() {
    const { width, children } = this.props;
    const { isDragging } = this.state;

    const scrollComponentStyle = {
      width: `${width}px`,
      minWidth: `${width}px`,
      cursor: isDragging ? 'move' : 'default',
    };

    return (
      <div
        ref={this.refHandler}
        data-testid="scroll-element"
        className="rct-scroll"
        style={scrollComponentStyle}
        onMouseDown={this.handleMouseDown}
        onMouseMove={this.handleMouseMove}
        onMouseUp={this.handleMouseUp}
        onMouseLeave={this.handleMouseLeave}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        onTouchEnd={this.handleTouchEnd}
        onScroll={this.handleScroll}
      >
        {children}
      </div>
    );
  }
}

export default ScrollElement;
